/**
 * Utilitários de cor das configurações de marca (cores hex vindas do layout da empresa).
 */

export type RgbColor = [number, number, number]

/** Converte `#rgb`/`#rrggbb` (com ou sem `#`) em RGB; `undefined` se não for hex válido. */
export const parseHexColor = (hex?: string): RgbColor | undefined => {
  const match = /^#?([a-f\d]{3}|[a-f\d]{6})$/i.exec(hex?.trim() ?? '')
  if (!match) return undefined

  const digits =
    match[1].length === 3
      ? match[1]
          .split('')
          .map((digit) => digit + digit)
          .join('')
      : match[1]

  return [0, 2, 4].map((start) => parseInt(digits.slice(start, start + 2), 16)) as RgbColor
}

/** Formato `r, g, b` usado nas variáveis `--bs-*-rgb` do Bootstrap (para `rgba()`). */
export const toRgbString = ([r, g, b]: RgbColor): string => `${r}, ${g}, ${b}`

/** Luminância relativa (WCAG 2.x). */
export const getRelativeLuminance = (rgb: RgbColor): number => {
  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Razão de contraste WCAG entre duas cores (1 a 21). */
export const getContrastRatio = (a: RgbColor, b: RgbColor): number => {
  const la = getRelativeLuminance(a)
  const lb = getRelativeLuminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

const LIGHT_FOREGROUND = '#ffffff'
const DARK_FOREGROUND = '#071437' // gray-900 do Metronic
const LIGHT_FOREGROUND_RGB: RgbColor = [255, 255, 255]
const DARK_FOREGROUND_RGB: RgbColor = [7, 20, 55]

/** Cor de texto (clara ou escura) com mais contraste sobre o fundo informado. */
export const getReadableForeground = (background: RgbColor): string =>
  getContrastRatio(background, LIGHT_FOREGROUND_RGB) >=
  getContrastRatio(background, DARK_FOREGROUND_RGB)
    ? LIGHT_FOREGROUND
    : DARK_FOREGROUND
